/**
 * API client.
 *
 * `credentials: "include"` so the httpOnly session cookie set by the OAuth
 * callback rides along. The API's CORS config allows credentials for the SPA
 * origin (see serverless.yml httpApi.cors).
 */
import { config } from "./config";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init.headers },
    ...init,
  });
  if (!res.ok) {
    // Surface the API's own error message where there is one.
    let detail = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; detail?: string };
      if (body.error) detail = body.detail ? `${body.error}: ${body.detail}` : body.error;
    } catch {
      /* non-JSON body — keep the status */
    }
    throw new ApiError(res.status, detail);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Full URL to an API route — for links that need a real navigation. */
export function apiUrl(path: string): string {
  return `${config.apiBaseUrl}${path}`;
}

// ── Types (mirror api/src/lib/dto.ts) ───────────────────────────────────────

export type Section = "paid" | "free" | "completed";
export type RequestStatus = "queued" | "complete" | "skipped";

export interface RequestDto {
  id: string;
  /** Absent for an unresolved chat-command request — see `title`. */
  youtubeUrl?: string;
  /** Raw chat text when no video link resolved. Exactly one of the two is set. */
  title?: string;
  requester: { platform: string; username: string };
  matchedCents: number;
  paid: boolean;
  section: Section;
  status: RequestStatus;
  position: number;
  manuallyPositioned: boolean;
  carriedOver: boolean;
  donationIds: string[];
  notes?: string;
  createdAt: string;
  completedAt?: string;
}

export interface DonationDto {
  id: string;
  platform: string;
  donorUsername: string;
  amountCents: number;
  currency: string;
  message?: string;
  donatedAt: string;
  matchedRequestId?: string;
  creditedAt?: string;
  linkable?: boolean;
  notes?: string;
}

export type ChannelRole = "streamer" | "mod";

/** A channel the signed-in account can access (own or modded). */
export interface Channel {
  channelId: string;
  displayName: string;
  role: ChannelRole;
  isOwn: boolean;
}

export interface ChannelMembers {
  members: { accountId: string; role: ChannelRole; displayName: string }[];
  invites: { platform: "twitch" | "youtube" | "discord"; handle: string }[];
}

/** Corner an overlay element ("now playing" text or the video) is anchored to. */
export type OverlayCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface Settings {
  paidThresholdCents: number;
  paidOrdering: "fifo" | "newest_first" | "amount_desc";
  freeOrdering: "fifo" | "newest_first";
  /** Cap on how many FREE requests one requester may have active at once. Paid requests are exempt. */
  maxRequestsPerUser: number;
  /** Chat command that auto-adds a free request (e.g. "!req"). Empty ⇒ off. */
  chatCommand: string;
  /** Chat command that replies with the chatter's queue position. Empty ⇒ off. */
  positionCommand: string;
  /** Reply template for a successful `chatCommand` add. Placeholder: `{user}`. */
  vrReplyTemplate: string;
  /**
   * Reply template for `positionCommand` when the chatter has an entry.
   * Placeholders: `{user}` `{title}` `{position}` `{ahead}` `{more}`.
   */
  posReplyTemplate: string;
  /**
   * Reply template for `chatCommand` when the chatter is already at
   * `maxRequestsPerUser`. Placeholders: `{user}` `{limit}`.
   */
  maxReachedReplyTemplate: string;
  /**
   * Cap on the total number of active (paid + free, not yet completed)
   * requests the queue may hold. 0 ⇒ unlimited. Paid requests are exempt,
   * same as `maxRequestsPerUser`.
   */
  maxQueueRequests: number;
  /**
   * Reply template for `chatCommand` when the queue is at `maxQueueRequests`.
   * Placeholders: `{user}` `{limit}`.
   */
  queueFullReplyTemplate: string;
  /** Whether the request command currently accepts new requests. Off ⇒ closed to viewers. */
  requestsEnabled: boolean;
  /**
   * Reply template for `chatCommand` while `requestsEnabled` is off, sent
   * instead of adding a request. Placeholder: `{user}`.
   */
  requestsClosedReplyTemplate: string;
  /**
   * Seconds — cap on a chat-added FREE request's video length. Paid requests
   * are exempt.
   */
  freeVideoLimitSeconds: number;
  /**
   * Reply template for `chatCommand` when the requested video is over
   * `freeVideoLimitSeconds`. Placeholders: `{user}` `{limit}` (minutes).
   */
  videoTooLongReplyTemplate: string;
  /** Trusted donation-bot chat account to parse (Streamlabs/StreamElements). Empty ⇒ off. */
  donationBotProvider: "streamlabs" | "streamelements";
  donationBotUsername: string;
  /** Cents credited when the YouTube chat crawler sees a chatter become a new channel member. 0 ⇒ off. */
  membershipDonationCents: number;
  /** Cents credited when the Twitch chat crawler sees a subscription. 0 ⇒ off. */
  twitchSubDonationCents: number;
  /** Cents credited per gifted sub when the Twitch chat crawler sees a gift-sub batch. 0 ⇒ off. */
  twitchGiftSubDonationCents: number;
  chatIngestEnabled: boolean;
  /** StreamView overlay (issue #9). Absent ⇒ never generated an OBS URL. */
  overlayToken?: string;
  overlayFont: string;
  /** Pixels — the now-playing title text only, not its small uppercase label. */
  overlayFontSize: number;
  overlayLayout: { nowPlaying: OverlayCorner; video: OverlayCorner };
  /** Unmuted by default — see OverlayCanvas.tsx for why streamers can mute it. */
  overlayMuted: boolean;
  /** Whether the video embed autoplays on the live overlay page. Off by default. */
  overlayAutoplay: boolean;
  /** Whether the Queue tab's now-playing card shows a play/pause overlay control. Off by default. */
  nowPlayingPlaybackEnabled: boolean;
  overlayVideoOpacity: number;
  /** Percent of the overlay canvas's width, not pixels — see OverlayCanvas.tsx. */
  overlayVideoWidth: number;
  /** Pixels — caps how wide the now-playing box can grow to fit a long title. Height follows content. */
  overlayNowPlayingWidth: number;
}

/** Minimal public shape served by GET /overlay/{token} — no auth, no PII. */
export interface OverlayReadResponse {
  nowPlaying: { title?: string; youtubeUrl?: string } | null;
  layout: { nowPlaying: OverlayCorner; video: OverlayCorner };
  font: string;
  fontSize: number;
  muted: boolean;
  autoplay: boolean;
  videoOpacity: number;
  /** Percent of the overlay canvas's width, not pixels — see OverlayCanvas.tsx. */
  videoWidth: number;
  /** Pixels — caps how wide the now-playing box can grow to fit a long title. */
  nowPlayingWidth: number;
}

export interface ChatPlatformStatus {
  connected: boolean;
  /** YouTube only: bound but waiting for the streamer to go live. */
  pending?: boolean;
}

export interface ChatStatus {
  chatCommand: string;
  positionCommand: string;
  chatIngestEnabled: boolean;
  /** Both platforms can be bound at once (a Twitch+YouTube simulcast). */
  platforms: Record<"twitch" | "youtube", ChatPlatformStatus>;
}

/** One ingested chat line pushed over the WS for the live feed (mirrors notify.ts). */
export interface ChatFeedMessage {
  platform: "twitch" | "youtube";
  username: string;
  text: string;
  /** True when this line matched the command and created a request. */
  matched: boolean;
  at: string;
  id: string;
}

export interface QueueResponse {
  nowPlaying: RequestDto | null;
  paid: RequestDto[];
  free: RequestDto[];
  completed: RequestDto[];
  completedTruncated: boolean;
  settings: Settings;
}

/** Health of one linked provider's stored OAuth connection (see session.ts). */
export interface Connection {
  platform: "twitch" | "youtube";
  connected: boolean;
  hasChatScope: boolean;
  /** ISO expiry of the stored access token, or null if it doesn't expire. */
  expiresAt: string | null;
  expired: boolean;
}

export interface Me {
  accountId: string;
  displayName: string;
  role: "streamer" | "admin";
  whitelisted: boolean;
  /** The provider identity this session is currently authenticated with. */
  platform: "twitch" | "youtube";
  /** One entry per linked provider (1–2). */
  connections: Connection[];
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export const api = {
  me: () => call<Me>("/me"),

  /** Detach a provider from the account (requires ≥2 linked; 409 use_delete otherwise). */
  unlinkProvider: (platform: "twitch" | "youtube") =>
    call<{ ok: boolean; loggedOut: boolean }>("/account/unlink", {
      method: "POST",
      body: JSON.stringify({ platform }),
    }),

  /** Permanently delete the whole account. Clears the session server-side. */
  deleteAccount: () =>
    call<{ ok: boolean }>("/account/delete", { method: "POST" }),

  /** Channels I can access (own + modded) — powers the channel switcher. */
  channels: () => call<{ channels: Channel[] }>("/channels"),

  /** Streamer-only: this channel's members + pending mod invites. */
  channelMembers: (channelId: string) =>
    call<ChannelMembers>(`/channels/members?channelId=${encodeURIComponent(channelId)}`),

  /** Streamer-only: grant mod by platform handle (pending until they log in). */
  grantMod: (channelId: string, platform: "twitch" | "youtube" | "discord", handle: string) =>
    call<{ ok: boolean }>("/channels/grant-mod", {
      method: "POST",
      body: JSON.stringify({ channelId, platform, handle }),
    }),

  /** Streamer-only: revoke a member (by accountId) or a pending invite (by handle). */
  revokeMod: (
    channelId: string,
    target: { accountId: string } | { platform: "twitch" | "youtube" | "discord"; handle: string },
  ) =>
    call<{ ok: boolean }>("/channels/revoke-mod", {
      method: "POST",
      body: JSON.stringify({ channelId, ...target }),
    }),

  /** Streamer-only: end the channel's live broadcast. */
  endBroadcast: (channelId: string) =>
    call<{ ok: boolean }>("/broadcast/end", {
      method: "POST",
      body: JSON.stringify({ channelId }),
    }),

  queue: (channelId: string) =>
    call<QueueResponse>(`/queue?channelId=${encodeURIComponent(channelId)}`),

  donations: (channelId: string, unmatchedOnly = false) =>
    call<{ items: DonationDto[] }>(
      `/donations?channelId=${encodeURIComponent(channelId)}` +
        (unmatchedOnly ? "&unmatched=true" : ""),
    ),

  addRequest: (body: {
    channelId: string;
    videoQuery: string;
    platform: string;
    username: string;
    notes?: string;
  }) => call<{ request: RequestDto }>("/requests", {
    method: "POST",
    body: JSON.stringify(body),
  }),

  addDonation: (body: {
    channelId: string;
    platform: string;
    donorUsername: string;
    amountCents: number;
    message?: string;
  }) =>
    call<{ donation: DonationDto; matchedRequest: RequestDto | null; heldAsCredit: boolean }>(
      "/donations",
      { method: "POST", body: JSON.stringify(body) },
    ),

  /** Queue operations: complete | skip | remove | reactivate | move | setMatched | setNotes | setLink | pin | link | unlink | removeDonation | setDonationNotes | clearDonations | reconcile. */
  queueOp: (body: Record<string, unknown>) =>
    call<{ request?: RequestDto; donation?: DonationDto; ok?: boolean }>("/queue/update", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getSettings: (channelId: string) =>
    call<{ settings: Settings }>(`/settings?channelId=${encodeURIComponent(channelId)}`),

  putSettings: (body: Partial<Settings> & { channelId: string }) =>
    call<{ settings: Settings }>("/settings", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Mint a new overlay token, invalidating whatever's pasted into OBS today. */
  regenerateOverlayToken: (channelId: string) =>
    call<{ overlayToken: string }>("/overlay/token/regenerate", {
      method: "POST",
      body: JSON.stringify({ channelId }),
    }),

  /** Remote play/pause for the now-playing embed on the overlay page(s). */
  overlayControl: (channelId: string, action: "play" | "pause") =>
    call<{ ok: boolean }>("/overlay/control", {
      method: "POST",
      body: JSON.stringify({ channelId, action }),
    }),

  /** Public read for the OBS overlay page — no session cookie needed. */
  overlay: (token: string) =>
    call<OverlayReadResponse>(`/overlay/${encodeURIComponent(token)}`),

  // — Chat ingestion —
  chatStatus: (channelId: string) =>
    call<ChatStatus>(`/chat/status?channelId=${encodeURIComponent(channelId)}`),

  chatConnect: (channelId: string, platform: "twitch" | "youtube") =>
    call<{ ok: boolean; platform: string }>("/chat/connect", {
      method: "POST",
      body: JSON.stringify({ channelId, platform }),
    }),

  chatDisconnect: (channelId: string, platform: "twitch" | "youtube") =>
    call<{ ok: boolean }>("/chat/disconnect", {
      method: "POST",
      body: JSON.stringify({ channelId, platform }),
    }),

  /** LOCAL-ONLY: inject a fake chat line to preview the feed (404s off local). */
  chatDevInject: (body: { channelId: string; text: string; username?: string }) =>
    call<{ ok: boolean }>("/chat/dev-inject", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Batched video-id → duration-in-seconds lookup for the thumbnail badge. */
  videoDurations: (ids: string[]) =>
    call<{ durations: Record<string, number> }>(
      `/youtube/durations?ids=${ids.map(encodeURIComponent).join(",")}`,
    ),
};
